import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip';
import ScrollEdge from '@components/RingilComponents/ScrollEdge';
import ScrollEdgeArrow from '@components/RingilComponents/ScrollEdgeArrow';
import SvgCheck from '@icons/check.svg?react';
import SvgSortAscending from '@icons/sort-ascending-filled.svg?react';
import SvgSortDescending from '@icons/sort-descending-filled.svg?react';
import {
  addWidth,
  ariaSortFor,
  CHECKBOX_SLOT,
  ColumnConfigV3,
  DRAG_MIN_WIDTH,
  dragWidth,
  headingText,
  isTypingTarget,
  keyScrollDelta,
  nextSort,
  pageScrollStep,
  pinnedShadows,
  rowClickIntent,
  rowTone,
  RowTone,
  selectAllState,
  StickyPlacement,
  stickyPlacements,
  tableMinWidth,
  TableSort,
} from './compactTableLogic';

interface CompactTableProps<TRow> {
  rows: TRow[];
  /** Already filtered and ordered — see `visibleColumns`. */
  columns: ColumnConfigV3<TRow>[];
  rowKey: (row: TRow, index: number) => string | number;
  sort: TableSort;
  onSort: (sort: TableSort) => void;
  loading: boolean;
  emptyMessage: React.ReactNode;
  /** Draws the row checkboxes (inside the first column) and select-all; off without mass actions. */
  showSelection: boolean;
  isRowSelectable?: (row: TRow) => boolean;
  isSelected: (row: TRow) => boolean;
  onToggleSelected: (row: TRow) => void;
  /** Select-all in the header: selects every selectable row on the page, or clears them. */
  onToggleAll: (select: boolean) => void;
  isExpanded: (row: TRow) => boolean;
  toggleExpandedRow: (row: TRow) => void;
  rowExpansionTemplate?: (row: TRow, toggleExpandedRow?: (row: TRow) => void) => React.ReactNode;
  /**
   * The user dragged a `resizable` column's header edge to `width` px (the column's own width, the row
   * checkbox not counted), or double-clicked it to take it back to its derived width (`undefined`).
   */
  onResizeColumn?: (columnId: string, width: number | undefined) => void;
  /** Split view: the row open in the detail pane — drawn like the Figma "Row active" state. */
  activeRowKey?: string | number;
  /** Split view: a row was clicked to be opened in the pane (see `rowClickIntent`). */
  onRowClick?: (row: TRow) => void;
}

/*
 * Looks from the Ringil 2.0 table (Figma Web-App-2.0 121:7401, CO Table Cell). Every cell carries its
 * own background: a pinned cell must cover what scrolls under it, and the row's hover/selected tint
 * must reach the pinned cells too, so neither can come from the <tr>.
 */
const headCell =
  'h-[30px] bg-content-quaternary pl-3 pr-2 text-left align-middle whitespace-nowrap overflow-hidden font-medium text-sm leading-5 text-content-secondary border-0';
const bodyCell = 'pl-3 pr-2 py-2 border-0 border-b border-solid border-content-quaternary';
// Zebra rows; hover is the dropdown items' hover (RingilDropdown) on every row, selected or not.
const TONE_CLASS: Record<RowTone, string> = { plain: 'bg-base-secondary', stripe: 'bg-base-tertiary', selected: 'bg-base-quaternary' };
const rowTint = (tone: RowTone) => clsx(TONE_CLASS[tone], 'group-hover:bg-content-quaternary');

const stickyStyle = (placement?: StickyPlacement): React.CSSProperties | undefined =>
  placement ? { position: 'sticky', [placement.side]: placement.offset, zIndex: 1 } : undefined;

/** The 16px CO Checkbox: hidden until the row is hovered (or anything is selected), filled when checked. */
const Checkbox: React.FC<{ state: 'none' | 'some' | 'all'; alwaysVisible: boolean; onChange: () => void; label: string }> = ({
  state,
  alwaysVisible,
  onChange,
  label,
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={state === 'some' ? 'mixed' : state === 'all'}
    aria-label={label}
    onClick={onChange}
    className={clsx(
      'mt-0.5 w-4 h-4 shrink-0 p-0 rounded-sm border border-solid flex items-center justify-center cursor-pointer transition-opacity focus-visible:opacity-100',
      state === 'none' ? 'bg-base-secondary border-content-tertiary hover:border-content-brand' : 'bg-content-brand border-content-brand text-content-negativePrimary',
      state !== 'none' || alwaysVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
    )}
  >
    {state === 'all' && <SvgCheck className="w-2.5 h-2.5" />}
    {state === 'some' && <span className="block w-2 h-0.5 bg-content-negativePrimary" />}
  </button>
);

/**
 * The grid of RingilDataTable v3: a plain table styled with Tailwind, so the compact look, pinned
 * columns and — in later phases — row activation and stacked columns are ours to shape instead of
 * overrides on PrimeReact's table. Data, paging, filters and selection state stay in
 * {@link RingilDataTableV3}; this only draws them.
 *
 * Columns have fixed widths (`table-layout: fixed`); only a `fill` column takes what is left. A cell's
 * content wraps or truncates inside its column and never widens it — the table scrolls sideways
 * instead once the columns no longer fit.
 */
const CompactTable = <TRow,>({
  rows,
  columns,
  rowKey,
  sort,
  onSort,
  loading,
  emptyMessage,
  showSelection,
  isRowSelectable,
  isSelected,
  onToggleSelected,
  onToggleAll,
  isExpanded,
  toggleExpandedRow,
  rowExpansionTemplate,
  onResizeColumn,
  activeRowKey,
  onRowClick,
}: CompactTableProps<TRow>) => {
  const { tr } = useTranslationLgs();
  const scrollerRef = useRef<HTMLDivElement>(null);
  // The header is a table of its own in a sticky bar, so it stays on screen while the pane scrolls down
  // (a sticky <thead> can't: the sideways scroller would be what it sticks to). It follows the body sideways.
  const headRef = useRef<HTMLDivElement>(null);
  // CustomTooltip finds its target by selector; useId's colons aren't valid in one.
  const resizeIdBase = `column-resize-${useId().replace(/:/g, '-')}`;
  // Where columns are hidden under the pinned ones (or past the scroller's edge when nothing is pinned),
  // the menu's scroll edge marks it; insets put it on the pinned columns' inner side, above the scrollbar.
  const [edges, setEdges] = useState({ left: false, right: false, leftInset: 0, rightInset: 0, bottomInset: 0 });
  // While a header edge is dragged the column follows the pointer here; the width is handed out only
  // on release, so the tab isn't rewritten on every pointer move.
  const [liveWidths, setLiveWidths] = useState<Record<string, number>>({});
  const dragRef = useRef<{ columnId: string; startX: number; startWidth: number; min: number } | null>(null);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (headRef.current) headRef.current.scrollLeft = el.scrollLeft;
    const pinnedWidth = (side: 'left' | 'right') =>
      Math.max(0, ...Array.from(headRef.current?.querySelectorAll<HTMLElement>(`th[data-pinned="${side}"]`) ?? [], th => th.offsetWidth));
    const next = { ...pinnedShadows(el), leftInset: pinnedWidth('left'), rightInset: pinnedWidth('right'), bottomInset: el.offsetHeight - el.clientHeight };
    setEdges(current => ((Object.keys(next) as (keyof typeof next)[]).every(key => current[key] === next[key]) ? current : next));
  }, []);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    if (headRef.current?.firstElementChild) observer.observe(headRef.current.firstElementChild);
    return () => observer.disconnect();
  }, [measure, columns.length, rows.length]);

  const columnsIn = columns;
  columns = columnsIn.map(column => (liveWidths[column.id] ? { ...column, width: `${liveWidths[column.id]}px`, fill: false } : column));
  // The active row follows ↑/↓, so keep it in view inside the scrolling list pane.
  useLayoutEffect(() => {
    if (activeRowKey === undefined) return;
    scrollerRef.current?.querySelector<HTMLElement>('tr[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeRowKey]);

  const placements = stickyPlacements(columns);
  const allState = selectAllState(rows, isSelected, isRowSelectable);
  const anySelected = showSelection && allState !== 'none';
  const firstColumnId = columns[0]?.id;

  // The row checkbox lives in the first cell; that column grows by it instead of squeezing its content.
  const withCheckbox = (column: ColumnConfigV3<TRow>) => (showSelection && column.id === firstColumnId ? addWidth(column.width, CHECKBOX_SLOT) : column.width);
  const heading = (column: ColumnConfigV3<TRow>) => (column.headerRender ? column.headerRender() : headingText(column, tr));
  // A heading cut off by a narrow column ends in an ellipsis and shows in full on hover.
  const headingLabel = (column: ColumnConfigV3<TRow>) => {
    const content = heading(column);
    return typeof content === 'string' ? <EllipsisText className="min-w-0">{content}</EllipsisText> : <span className="truncate">{content}</span>;
  };
  const startResize = (event: React.PointerEvent<HTMLSpanElement>, column: ColumnConfigV3<TRow>) => {
    event.preventDefault();
    event.stopPropagation();
    const th = event.currentTarget.parentElement;
    const checkbox = showSelection && column.id === firstColumnId ? CHECKBOX_SLOT : 0;
    dragRef.current = {
      columnId: column.id,
      startX: event.clientX,
      startWidth: (th?.getBoundingClientRect().width ?? 0) - checkbox,
      min: column.minResizeWidth ?? DRAG_MIN_WIDTH,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveResize = (event: React.PointerEvent<HTMLSpanElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    setLiveWidths({ [drag.columnId]: dragWidth(drag.startWidth, drag.startX, event.clientX, drag.min) });
  };
  const endResize = (event: React.PointerEvent<HTMLSpanElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    const width = dragWidth(drag.startWidth, drag.startX, event.clientX, drag.min);
    setLiveWidths({});
    if (width !== Math.round(drag.startWidth)) onResizeColumn?.(drag.columnId, width);
  };

  const scrollSideways = (delta: number, smooth = true) => scrollerRef.current?.scrollBy({ left: delta, behavior: smooth ? 'smooth' : 'auto' });
  const scrollPage = (direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (el) scrollSideways(direction * pageScrollStep(el.clientWidth, edges.leftInset, edges.rightInset));
  };
  // ←/→ scroll the columns while focus is anywhere in the table (↑/↓ are the split view's, see RingilDataTableV3).
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const delta = keyScrollDelta(event, isTypingTarget(event.target as HTMLElement));
    if (!delta || !edges[delta < 0 ? 'left' : 'right']) return;
    event.preventDefault();
    // Smooth steps would queue up behind a held key and lag; only a single press glides.
    scrollSideways(delta, !event.repeat);
  };
  // The header can't be scrolled itself (it only follows the body), so a sideways swipe over it is handed on.
  const onHeaderWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const dx = event.deltaX || (event.shiftKey ? event.deltaY : 0);
    if (dx && scrollerRef.current) scrollerRef.current.scrollLeft += dx;
  };

  // Header and body are two tables sharing these, so their columns line up.
  const tableClass = 'w-full table-fixed border-separate border-spacing-0 text-sm leading-5 text-content-primary';
  const tableStyle = { minWidth: tableMinWidth(columns) + (showSelection ? CHECKBOX_SLOT : 0) };
  const colgroup = (
    <colgroup>
      {columns.map(column => (
        <col key={column.id} style={column.fill ? undefined : { width: withCheckbox(column) }} />
      ))}
    </colgroup>
  );
  const sideEdges = (style?: React.CSSProperties) =>
    (['left', 'right'] as const).map(side => (
      <ScrollEdge key={side} edge={side} visible={edges[side]} inset={side === 'left' ? edges.leftInset : edges.rightInset} style={style} />
    ));

  return (
    <div className="relative outline-none font-['DM_Sans']" tabIndex={-1} onKeyDown={onKeyDown} data-compact-table>
      <div className="sticky top-0 z-[2]" onWheel={onHeaderWheel}>
        <div ref={headRef} className="relative isolate overflow-hidden">
          <table className={tableClass} style={tableStyle}>
            {colgroup}
            <thead>
              <tr>
                {columns.map((column, columnIndex) => {
                  const placement = placements[column.id];
                  const sortable = column.sortable && !!column.field;
                  const sorted = sortable && sort.sortField === column.field;
                  return (
                    <th
                      key={column.id}
                      scope="col"
                      aria-sort={ariaSortFor(column, sort)}
                      // z-0: its own stacking context, so the resize handle's z-index can't lift it over a pinned cell scrolled above it.
                      className={clsx(headCell, 'relative z-0')}
                      style={stickyStyle(placement)}
                      data-pinned={placement?.side}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {showSelection && column.id === firstColumnId && (
                          <Checkbox state={allState} alwaysVisible onChange={() => onToggleAll(allState !== 'all')} label={tr('generic.selectAll', 'Select all')} />
                        )}
                        {sortable ? (
                          <button
                            type="button"
                            onClick={() => onSort(nextSort(sort, column.field))}
                            className="inline-flex min-w-0 items-center gap-1 p-0 bg-transparent border-0 cursor-pointer font-[inherit] text-[length:inherit] leading-[inherit] text-content-secondary hover:text-content-primary"
                          >
                            {headingLabel(column)}
                            {sorted && sort.sortDirection === 'ASC' && <SvgSortAscending className="w-3 h-3 shrink-0 text-content-brand" />}
                            {sorted && sort.sortDirection === 'DESC' && <SvgSortDescending className="w-3 h-3 shrink-0 text-content-brand" />}
                            {!sorted && <SvgSortDescending className="w-3 h-3 shrink-0 text-content-tertiary" />}
                          </button>
                        ) : (
                          headingLabel(column)
                        )}
                      </div>
                      {onResizeColumn && column.resizable && (
                        <>
                          <span
                            id={`${resizeIdBase}-${columnIndex}`}
                            role="separator"
                            aria-orientation="vertical"
                            aria-label={tr('RingilDataTable.resizeColumnHint', 'Drag to change the width, double-click to reset it')}
                            onPointerDown={event => startResize(event, column)}
                            onPointerMove={moveResize}
                            onPointerUp={endResize}
                            onDoubleClick={() => onResizeColumn(column.id, undefined)}
                            // A short divider centred in the handle keeps it discoverable at rest; hovering lights up the whole handle around it.
                            className="absolute right-0 top-0 z-[2] h-full w-1.5 cursor-col-resize touch-none hover:bg-content-brand after:absolute after:left-1/2 after:top-1/2 after:h-4 after:w-px after:-translate-x-1/2 after:-translate-y-1/2 after:bg-content-brand after:content-[''] hover:after:hidden"
                            data-testid={`column-resize-${column.id}`}
                          />
                          <CustomTooltip
                            targetSelector={`#${resizeIdBase}-${columnIndex}`}
                            content={tr('RingilDataTable.resizeColumnHint', 'Drag to change the width, double-click to reset it')}
                          />
                        </>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
        </div>
        {/* Clickable twins of the scroll edges, on the header only so they never sit over a row. */}
        <div className="pointer-events-none absolute inset-y-0 [&>*]:pointer-events-auto" style={{ left: edges.leftInset, right: edges.rightInset }}>
          {edges.left && (
            <ScrollEdgeArrow
              side="left"
              surface="header"
              tooltip={tr('RingilDataTable.scrollLeft', 'Scroll left')}
              onClick={() => scrollPage(-1)}
              dataTestId="table-scroll-left"
            />
          )}
          {edges.right && (
            <ScrollEdgeArrow
              side="right"
              surface="header"
              tooltip={tr('RingilDataTable.scrollRight', 'Scroll right')}
              onClick={() => scrollPage(1)}
              dataTestId="table-scroll-right"
            />
          )}
        </div>
        {sideEdges()}
      </div>
      <div className="relative">
        <div ref={scrollerRef} onScroll={measure} className={clsx('relative isolate overflow-x-auto bg-base-secondary', loading && rows.length === 0 && 'min-h-[96px]')}>
          {/* The frame around the table draws the bottom edge, so the last row drops its own line. */}
          <table className={clsx(tableClass, '[&>tbody>tr:last-child>td]:border-b-0')} style={tableStyle}>
            {colgroup}
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={columns.length} className="border-0">
                    {emptyMessage}
                  </td>
                </tr>
              )}
              {rows.map((row, index) => {
                const selectable = !isRowSelectable || isRowSelectable(row);
                const selected = showSelection && isSelected(row);
                const key = rowKey(row, index);
                const active = activeRowKey !== undefined && key === activeRowKey;
                const expanded = !!rowExpansionTemplate && isExpanded(row);
                return (
                  <React.Fragment key={rowKey(row, index)}>
                    <tr
                      // scroll-mt: the sticky header's height, so a row brought into view by ↑/↓ isn't left under it.
                      className={clsx('group scroll-mt-[30px]', onRowClick && 'cursor-pointer')}
                      aria-selected={showSelection ? selected : undefined}
                      aria-current={active ? 'true' : undefined}
                      data-active={active ? 'true' : undefined}
                      onClick={
                        onRowClick
                          ? event => {
                              const intent = rowClickIntent(event.target as HTMLElement, event);
                              if (intent === 'ignore') return;
                              if (intent === 'activate-instead-of-link') event.preventDefault();
                              onRowClick(row);
                            }
                          : undefined
                      }
                    >
                      {columns.map(column => {
                        const placement = placements[column.id];
                        const first = column.id === firstColumnId;
                        return (
                          <td
                            key={column.id}
                            className={clsx(
                              bodyCell,
                              rowTint(rowTone(index, selected, active)),
                              column.verticalAlign === 'middle' ? 'align-middle' : 'align-top',
                              first && 'relative',
                            )}
                            style={stickyStyle(placement)}
                          >
                            {first && (selected || active) && <span className="absolute left-0 top-0 -bottom-px w-[3px] bg-content-brand" aria-hidden />}
                            {/* Text truncates inside its field (EllipsisText, full text on hover) and components keep their
                            shape — a column can't be dragged narrower than they need. The clip is only a last resort. */}
                            <div className={clsx('min-w-0 overflow-hidden', first && showSelection && 'flex items-start gap-2')}>
                              {first &&
                                showSelection &&
                                (selectable ? (
                                  <Checkbox
                                    state={selected ? 'all' : 'none'}
                                    alwaysVisible={anySelected}
                                    onChange={() => onToggleSelected(row)}
                                    label={tr('RingilDataTable.selectRow', 'Select row')}
                                  />
                                ) : (
                                  <span className="w-4 shrink-0" />
                                ))}
                              <div className="min-w-0 flex-1">{column.render(row, toggleExpandedRow)}</div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={columns.length} className="p-0 border-0 border-b border-solid border-content-quaternary bg-base-secondary">
                          {rowExpansionTemplate(row, toggleExpandedRow)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {loading && (
            <div className="absolute inset-0 z-[2] flex items-center justify-center bg-base-secondary/60" aria-busy="true">
              <i className="pi pi-spinner pi-spin text-2xl text-content-brand" />
            </div>
          )}
        </div>
        {sideEdges({ bottom: edges.bottomInset })}
      </div>
    </div>
  );
};

export default CompactTable;
